import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, UserPlus, Trash2, CheckCircle2 } from "lucide-react";

/**
 * REQ-WIPE-001 — Super Admin nuke-the-sandbox tool.
 *
 * Two-step, irreversible:
 *  1. Create a real Super Admin (so the caller doesn't lock themselves out
 *     after the wipe).
 *  2. Type "DELETE" to confirm — wipes every transactional collection plus
 *     all other users.
 */
export default function AdminWipeSampleDataPage() {
  const { user } = useAuth();
  const nav = useNavigate();

  // gate: super_admin only
  if (user && user.role !== "super_admin") {
    return (
      <div className="cc-card p-8 max-w-xl">
        <ShieldAlert className="w-8 h-8 text-[var(--cc-accent)] mb-3" />
        <h1 className="font-display text-2xl font-bold mb-2">Super Admin only</h1>
        <p className="text-[var(--cc-text-soft)]">
          The sample-data wipe is restricted to Super Admin accounts. Ask your
          Super Admin to perform this action.
        </p>
      </div>
    );
  }

  return (
    <div className="fade-up max-w-3xl">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Production reset</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3">
          <Trash2 className="w-8 h-8 text-[var(--cc-accent)]" /> Wipe sample data
        </h1>
        <p className="text-[var(--cc-text-soft)] mt-1 max-w-2xl">
          One-time tool to clear all seed jobs, candidates, demo users,
          audit / email / SMS logs, and uploaded evidence from the database.
          Use this exactly once after deploying to a brand-new production
          environment.
        </p>
      </div>

      <div className="cc-card p-4 mb-6 flex items-start gap-3" style={{ background: "#fef3f2", borderColor: "#f5c1bc" }}>
        <AlertTriangle className="w-5 h-5 mt-0.5 text-[var(--cc-accent)] flex-shrink-0" />
        <div className="text-sm text-[#7a271a]">
          <strong>This action is irreversible.</strong> All candidates, jobs, DBS / RTW
          uploads, references, interview templates, email / SMS log entries and
          every user account <em>except yours</em> will be permanently deleted. Make sure
          you have backups if you ever need this data.
        </div>
      </div>

      <BootstrapSuperAdminCard />

      <WipeCard onDone={() => nav("/login")} />
    </div>
  );
}

function BootstrapSuperAdminCard() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { email, action }

  const submit = async () => {
    if (!email || !firstName || !lastName || password.length < 8) {
      return toast.error("All fields required (password ≥ 8 chars)");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bootstrap-super-admin", {
        email, first_name: firstName, last_name: lastName, password, phone,
      });
      setDone({ email, action: data.action });
      toast.success(`Real Super Admin ${data.action} — save these credentials NOW`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create Super Admin");
    } finally { setBusy(false); }
  };

  return (
    <div className="cc-card p-6 mb-6" data-testid="bootstrap-super-admin-card">
      <h2 className="font-display text-xl font-bold mb-1 flex items-center gap-2">
        <UserPlus className="w-5 h-5" /> Step 1 — Create a real Super Admin
      </h2>
      <p className="text-sm text-[var(--cc-text-soft)] mb-4">
        This account will persist after the wipe so you don't get locked out.
        You can change the password and enable MFA on first login.
      </p>
      {done ? (
        <div className="p-4 rounded-lg flex items-center gap-3" style={{ background: "#e8f0ea", color: "#1f4d36" }} data-testid="bootstrap-success">
          <CheckCircle2 className="w-5 h-5" />
          <div className="text-sm">
            <strong>{done.email}</strong> {done.action}. You can now run Step 2.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Email</label>
            <input className="cc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="owner@rabahconcept.co.uk" data-testid="bootstrap-email" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">First name</label>
            <input className="cc-input" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                   data-testid="bootstrap-first-name" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Last name</label>
            <input className="cc-input" value={lastName} onChange={(e) => setLastName(e.target.value)}
                   data-testid="bootstrap-last-name" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Password (≥ 8 chars)</label>
            <input className="cc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="bootstrap-password" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Phone (optional)</label>
            <input className="cc-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                   data-testid="bootstrap-phone" />
          </div>
          <div className="col-span-2 flex justify-end">
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid="bootstrap-submit">
              {busy ? "Creating…" : "Create real Super Admin"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WipeCard({ onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleteUsers, setDeleteUsers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const canSubmit = confirmText === "DELETE" && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-sample-data", {
        confirmation: "DELETE",
        delete_users: deleteUsers,
      });
      setReport(data);
      toast.success("Sample data wiped — see report below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setBusy(false); }
  };

  if (report) {
    return (
      <div className="cc-card p-6" data-testid="wipe-report">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2" style={{ color: "#1f4d36" }}>
          <CheckCircle2 className="w-5 h-5" /> Wipe complete
        </h2>
        <div className="mb-4 p-3 rounded-lg bg-[var(--cc-surface)] text-sm">
          <div className="label-eyebrow mb-2">Records removed</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(report.deleted || {}).map(([k, v]) => (
                <tr key={k} className="border-b border-[var(--cc-border)] last:border-0">
                  <td className="py-1.5 pr-3 text-[var(--cc-text-soft)]">{k}</td>
                  <td className="py-1.5 font-mono font-semibold text-right">{v}</td>
                </tr>
              ))}
              <tr>
                <td className="py-1.5 pr-3 font-semibold">Remaining users</td>
                <td className="py-1.5 font-mono font-semibold text-right">{report.remaining_users}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="p-3 rounded-lg bg-[var(--cc-surface)] text-xs text-[var(--cc-text-soft)] mb-4">
          <strong>Next step:</strong> {report.note}
        </div>
        <button className="btn-primary" onClick={onDone} data-testid="wipe-done-button">
          Sign out and re-login with the new Super Admin
        </button>
      </div>
    );
  }

  return (
    <div className="cc-card p-6" data-testid="wipe-card">
      <h2 className="font-display text-xl font-bold mb-1 flex items-center gap-2" style={{ color: "var(--cc-accent)" }}>
        <Trash2 className="w-5 h-5" /> Step 2 — Confirm and wipe
      </h2>
      <p className="text-sm text-[var(--cc-text-soft)] mb-4">
        Type <code className="font-mono bg-[var(--cc-surface)] px-1.5 py-0.5 rounded">DELETE</code> below
        to enable the wipe button. The wipe will keep <em>only your current
        account</em>; all other users + all transactional data will be removed.
      </p>
      <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
        <input type="checkbox" checked={deleteUsers} onChange={(e) => setDeleteUsers(e.target.checked)}
               data-testid="wipe-delete-users" />
        Also delete every other user account (recommended)
      </label>
      <label className="label-eyebrow block mb-1">Type DELETE to confirm</label>
      <input
        className="cc-input mb-4 font-mono"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder="DELETE"
        data-testid="wipe-confirm-input"
      />
      <button
        className="btn-primary"
        style={{ background: canSubmit ? "var(--cc-accent)" : undefined, opacity: canSubmit ? 1 : 0.5 }}
        onClick={submit}
        disabled={!canSubmit}
        data-testid="wipe-submit-button"
      >
        {busy ? "Wiping…" : "Wipe all sample data permanently"}
      </button>
    </div>
  );
}
